import React, { useMemo, useEffect } from "react";
import { DatePicker, Pagination } from "antd";
import { formatToINR } from "../../../../helpers/utility";
import dayjs from "dayjs";
import useQuery from "../../../../hooks/useQuery";
import servicesIcon from "../../../../assets/receptionistIcons/ipBilling/services.svg";
import downloadIcon from "../../../../assets/receptionistIcons/ipBilling/download.svg";
import { printSummary } from "../../../Receptionist/receptionKeys";
import useReceptionistMobilePickerProps from "../../../Receptionist/receptionistMobilePickerProps";
import { updateUrlSearchParams, parsePageParam } from "../../../../helpers/urlFilters";
import { useNavigate } from "react-router-dom";
import styles from "./BillingIp.module.css";

const { RangePicker } = DatePicker;

const DETAILED_COLUMNS = ["Service", "Price", "Date"];
const SUMMARY_COLUMNS = ["Service", "Price", "Count", "Total"];
const SERVICES_PAGE_SIZE = 20;

const isServicesRightColumn = (col, summary) => {
  if (summary) return col === "Total";
  return col === "Price" || col === "Date";
};

const ServicesTableIp = ({
  billData,
  setDateFilter,
  dateFilter,
  activeTab,
  setActiveTab,
}) => {
  const params = useQuery();
  const navigate = useNavigate();
  const mobilePickerProps = useReceptionistMobilePickerProps({ range: true });
  const admissionDate = dayjs(billData?.data?.admission?.admission_date);
  const isSummary = activeTab === "summary";
  const servicesPage = parsePageParam(
    { page: params?.services_page },
    1
  );

  const dataSource =
    isSummary
      ? billData?.data?.service_details?.services
      : billData?.data?.detailed_services;

  const pagedRows = useMemo(() => {
    if (!dataSource?.length) return [];
    const start = (servicesPage - 1) * SERVICES_PAGE_SIZE;
    return dataSource.slice(start, start + SERVICES_PAGE_SIZE);
  }, [dataSource, servicesPage]);

  useEffect(() => {
    if (!dataSource?.length) return;
    const maxPage = Math.max(1, Math.ceil(dataSource.length / SERVICES_PAGE_SIZE));
    if (servicesPage > maxPage) {
      updateUrlSearchParams(navigate, { services_page: maxPage });
    }
  }, [dataSource, servicesPage, navigate]);

  const servicesSubtotal = useMemo(() => {
    if (!dataSource?.length) return 0;
    return dataSource.reduce((sum, row) => {
      const value = isSummary
        ? Number(row.total_price || 0)
        : Number(row.total_price || row.price || 0);
      return sum + value;
    }, 0);
  }, [dataSource, isSummary]);

  const disabledDate = (current) => {
    if (!current) return false;
    const today = dayjs().endOf("day");
    return (
      current.isBefore(admissionDate, "day") || current.isAfter(today, "day")
    );
  };

  const columns = isSummary ? SUMMARY_COLUMNS : DETAILED_COLUMNS;

  return (
    <section
      className={`${styles.servicesCard} ${
        isSummary ? styles.servicesCardSummary : styles.servicesCardDetailed
      }`}
    >
      <header className={styles.servicesCardHeader}>
        <div className={styles.servicesCardHeaderLeft}>
          <img src={servicesIcon} alt="" className={styles.servicesCardIcon} />
          <div>
            <h3 className={styles.sectionCardTitle}>Services billing</h3>
            <p className={styles.servicesSubtitle}>Admission store admission</p>
          </div>
        </div>
        <div className={styles.servicesHeaderTools}>
          <div className={styles.segmented} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={isSummary}
              className={`${styles.segmentBtn} ${
                isSummary ? styles.segmentBtnActive : ""
              }`}
              onClick={() => {
                setActiveTab("summary");
                updateUrlSearchParams(navigate, { services_page: 1 });
              }}
            >
              Summary
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isSummary}
              className={`${styles.segmentBtn} ${
                !isSummary ? styles.segmentBtnActive : ""
              }`}
              onClick={() => {
                setActiveTab("detailed");
                updateUrlSearchParams(navigate, { services_page: 1 });
              }}
            >
              Detailed
            </button>
          </div>
          {!isSummary && (
            <RangePicker
              className={styles.servicesDateRange}
              disabledDate={disabledDate}
              value={
                dateFilter ? [dateFilter.start_date, dateFilter.end_date] : null
              }
              onChange={(date) => {
                if (!date) {
                  setDateFilter(null);
                  return;
                }
                setDateFilter({
                  start_date: date[0],
                  end_date: date[1],
                });
              }}
              placeholder={["Start date", "End date"]}
              separator="→"
              inputReadOnly
              {...mobilePickerProps}
            />
          )}
          <button
            type="button"
            className={styles.downloadBtn}
            onClick={() => printSummary(params?.patient, activeTab, dateFilter)}
            aria-label={isSummary ? "Download summary" : "Download detailed bill"}
          >
            <img src={downloadIcon} alt="" />
          </button>
        </div>
      </header>

      <div className={styles.servicesBillingTableScroll}>
        <div className={styles.servicesTableHeader}>
          {columns.map((col) => (
            <span
              key={col}
              className={`${styles.servicesColLabel} ${
                isServicesRightColumn(col, isSummary)
                  ? styles.billingTableColEnd
                  : ""
              }`}
            >
              {col}
            </span>
          ))}
        </div>

        <div className={styles.servicesTableBody}>
          {!pagedRows?.length ? (
            <p className={styles.servicesEmpty}>No services found.</p>
          ) : (
            pagedRows.map((row, index) => (
              <div
                key={`${row.service}-${index}`}
                className={styles.servicesTableRow}
              >
                <span className={styles.billingTableCellText}>{row.service}</span>
                <span
                  className={`${styles.servicesCellMuted} ${
                    !isSummary ? styles.billingTableColEnd : ""
                  }`}
                >
                  {formatToINR(
                    isSummary ? row.price : row.total_price || row.price
                  )}
                </span>
                {isSummary ? (
                  <>
                    <span className={styles.servicesCellMuted}>
                      {row.count ?? "—"}
                    </span>
                    <span
                      className={`${styles.servicesCellMuted} ${styles.billingTableColEnd}`}
                    >
                      {formatToINR(row.total_price)}
                    </span>
                  </>
                ) : (
                  <span
                    className={`${styles.servicesCellMuted} ${styles.billingTableColEnd}`}
                  >
                    {row.date ? dayjs(row.date).format("D MMM YYYY") : "—"}
                  </span>
                )}
              </div>
            ))
          )}
        </div>

        {dataSource?.length > SERVICES_PAGE_SIZE ? (
          <div className={styles.servicesPagination}>
            <Pagination
              current={servicesPage}
              pageSize={SERVICES_PAGE_SIZE}
              total={dataSource.length}
              showSizeChanger={false}
              onChange={(p) =>
                updateUrlSearchParams(navigate, { services_page: p })
              }
            />
          </div>
        ) : null}

        <footer className={styles.servicesSubtotalRow}>
          <span className={styles.servicesSubtotalLabel}>Services subtotal</span>
          <span
            className={`${styles.servicesSubtotalAmount} ${styles.billingTableCellAmount} ${styles.billingTableColEnd}`}
          >
            {formatToINR(servicesSubtotal)}
          </span>
        </footer>
      </div>
    </section>
  );
};

export default ServicesTableIp;
